import { CustomMutationOptions, CustomQueryOptions } from '@/types/query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckHuggingFaceSupportParams,
  SpecAndPricingInstanceGPUWithDiscount,
  SpecAndPricingInstanceGPU,
  CreateDedicateInstanceRequest,
  GetInstancesRequest,
  GetInstancesResponse,
  RegenerateAPIDedicateInstanceRequest,
  GetInstanceRequest,
  GetInstanceResponse,
  DeleteDedicateInstanceRequest,
  GetUsageAndCosDedicateInstanceByIdRequest,
  GetUsageAndCosDedicateInstance,
  GetUsageLogsDedicateInstanceLLMRequest,
  GetUsageLogsDedicateInstanceLLMResponse,
  GetUsageAndCosDedicateInstancesRequest,
  StatusGetInstances,
} from './type';
import {
  checkHuggingFaceSupport,
  createDedicateInstance,
  deleteDedicateInstance,
  getAvailablePricing,
  getDedicateInstanceById,
  getDedicateInstances,
  getGetUsageLogsDedicateInstance,
  getUsageAndCostDedicateInstance,
  getUsageAndCostDedicateInstances,
  regenerateAPI,
} from '.';
import dayjs from 'dayjs';

const keyname = 'one-click-deploy';

export const useSendCheckHuggingFaceSupport = (
  options?: CustomMutationOptions<CheckHuggingFaceSupportParams>
) => {
  return useMutation({
    ...options,
    mutationFn: checkHuggingFaceSupport,
    // mutationFn: async () => {},
  });
};

export const useGetSpecAndPricingInstanceGPU = (
  option?: CustomQueryOptions<SpecAndPricingInstanceGPUWithDiscount[]>
) => {
  return useQuery({
    queryKey: [`${keyname}-spec-and-pricing-instance-gpu`],
    queryFn: async () => {
      function addDiscountInfo(
        items: SpecAndPricingInstanceGPU[]
      ): SpecAndPricingInstanceGPUWithDiscount[] {
        const groupedItems = items.reduce(
          (acc, item) => {
            const key = `${item.provider}-${item.region}-${item.gpu_id}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(item);
            return acc;
          },
          {} as Record<string, SpecAndPricingInstanceGPU[]>
        );

        const processedItems: SpecAndPricingInstanceGPUWithDiscount[] = [];

        for (const group of Object.values(groupedItems)) {
          const sortedGroup = group.sort((a, b) => b.priority - a.priority);

          const lowestPriority = sortedGroup[sortedGroup.length - 1];

          for (const item of sortedGroup) {
            const discount = lowestPriority.price - item.price;
            // const discountPercentage = (discount / lowestPriority.price) * 100;

            const processedItem: SpecAndPricingInstanceGPUWithDiscount = {
              ...item,
              isDiscounted: item.price !== lowestPriority.price,
              originalPrice: lowestPriority.price,
              discount: discount,
              // discountPercentage: Number(discountPercentage.toFixed(2)),
            };

            processedItems.push(processedItem);
          }
        }

        return processedItems;
      }
      const data = await getAvailablePricing();
      const result = addDiscountInfo(data?.pricing_info || []);
      return result;
    },
    ...option,
  });
};

export const useCreateDedicateInstance = (
  options?: CustomMutationOptions<CreateDedicateInstanceRequest>
) => {
  return useMutation({
    ...options,
    mutationFn: createDedicateInstance,
  });
};

export const useGetDedicateInstances = (
  params: GetInstancesRequest,
  option?: CustomQueryOptions<GetInstancesResponse>
) => {
  return useQuery({
    queryKey: [`${keyname}-dedicate-instances`, params?.workspace_id],
    queryFn: async () => getDedicateInstances(params),
    ...option,
  });
};

const getDedicatedInstanceQueryKey = (params: string) => [
  `${keyname}-dedicate-instances-by-id`,
  params,
];

export const useGetDedicateInstance = (
  params: GetInstanceRequest,
  option?: CustomQueryOptions<GetInstanceResponse>
) => {
  return useQuery({
    queryKey: getDedicatedInstanceQueryKey(params?.instance_id),
    // queryFn: async () => {
    //   return {
    //     instance_id: params.instance_id,
    //     instance_name: 'instances-name',
    //     instance_type: 'instance-type',
    //     model_repo: 'google/gemma-2-2b',
    //     status: 'running',
    //     created_at: dayjs().toISOString(),
    //     updated_at: dayjs().toISOString(),
    //     user_id: 'user_id',
    //     workspace_id: 'c58cc-1c3d-4df9-af29-9cdbb036749c',
    //     api_key: 'apikey',
    //     endpoint: 'http://52.221.183.215:8000/v1',
    //     instance_spec: {
    //       gpu_id: 'gpu_id',
    //       gpu_name: 'gpu_name',
    //       vram: 16,
    //       price: 0.5,
    //       priority: 1,
    //       provider: 'aws',
    //       region: 'us-east-1',
    //       is_available: true,
    //       pricing_id: '1',
    //       cuda_sm: 8,
    //     },
    //   } as GetInstanceResponse;
    // },
    queryFn: async () => getDedicateInstanceById(params),
    ...option,
  });
};

export const useRegenerateAPI = (
  options?: CustomMutationOptions<RegenerateAPIDedicateInstanceRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: regenerateAPI,
    onSuccess: async (data, variables, context) => {
      await options?.onSuccess?.(data, variables, context);

      await queryClient.setQueryData(
        getDedicatedInstanceQueryKey(variables.instance_id),
        (oldData: GetInstanceResponse | undefined) =>
          oldData ? { ...oldData, api_key: data.api_key } : undefined
      );
    },
  });
};

export const useDeleteDedicateInstance = (
  options?: CustomMutationOptions<DeleteDedicateInstanceRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: deleteDedicateInstance,
    onSuccess: async (data, variables, context) => {
      await options?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries({
        queryKey: getDedicatedInstanceQueryKey(variables.instance_id),
      });
      await queryClient.setQueryData(
        getDedicatedInstanceQueryKey(variables.instance_id),
        (oldData: GetInstanceResponse | undefined) =>
          oldData ? { ...oldData, status: StatusGetInstances.Terminated} : undefined
      );
    },
  });
};

export const useGetUsageAndCostDedicateInstance = (
  params: GetUsageAndCosDedicateInstanceByIdRequest,
  option?: CustomQueryOptions<GetUsageAndCosDedicateInstance>
) => {
  return useQuery({
    queryKey: [`${keyname}-usage-cost`, params],
    queryFn: async () => getUsageAndCostDedicateInstance(params),
    ...option,
  });
};

export const useGetUsageAndCostDedicateInstances = (
  params: GetUsageAndCosDedicateInstancesRequest,
  option?: CustomQueryOptions<GetUsageAndCosDedicateInstance[]>
) => {
  return useQuery({
    queryKey: [`${keyname}-usage-cost`, params],
    queryFn: async () => getUsageAndCostDedicateInstances(params),
    ...option,
  });
};

export const useGetUsageLogsDedicateInstance = (
  params: GetUsageLogsDedicateInstanceLLMRequest,
  option?: CustomQueryOptions<GetUsageLogsDedicateInstanceLLMResponse>
) => {
  return useQuery({
    queryKey: [`${keyname}-usage-log`, params],
    queryFn: async () => getGetUsageLogsDedicateInstance(params),
    // queryFn: async () => {
    //   return [
    //     {
    //       count: 10,
    //       timestamp: dayjs().toISOString(),
    //       total_prompt_usage: 950,
    //       average_time_to_complete: 2.5,
    //       total_completion_usage: 85,
    //     },
    //     {
    //       count: 16,
    //       timestamp: dayjs().subtract(3, 'day').toISOString(),
    //       total_prompt_usage: 100,
    //       average_time_to_complete: 2.5,
    //       total_completion_usage: 20,
    //     },
    //     {
    //       count: 16,
    //       timestamp: dayjs().subtract(5, 'day').toISOString(),
    //       total_prompt_usage: 100,
    //       average_time_to_complete: 2.5,
    //       total_completion_usage: 20,
    //     },
    //   ];
    // },
    ...option,
  });
};
